import { useState } from 'react'
import { CheckIcon, ChevronRightIcon, TrashIcon } from '../components/icons'
import { JobIcon } from '../components/JobIcon'
import { formatMoney, formatDuration, normalizePrice } from '../lib/format'
import { primaryService, type JobType } from '../lib/jobTypes'
import type { UpsellPrompt } from '../lib/upsells'
import { isUnconfirmedPrice, unconfirmedPriceLabel } from '../lib/vehiclePricing'
import type { BasketItem } from '../lib/services'
import type { WidgetBootstrapResponse } from '../types/PublicBookingChannelTypes'

export interface MotPrompt {
  /** Formatted expiry, e.g. "14 September 2026". */
  expiryLabel: string
  daysRemaining: number
  jobType: JobType
  /** The MOT service when it sits inside a wider category; see MotOffer. */
  service: WidgetBootstrapResponse.ServiceItem | null
  /** What the MOT costs, or null when it is unpriced. */
  fromPrice: number | null
}

/** The chosen tyre, which is not a basket item — see BookingWidget. */
export interface TyreLine {
  description: string
  total: number
}

export interface ExtrasStepProps {
  /** Job types not already in the booking. */
  available: JobType[]
  basket: BasketItem[]
  /** Adds or removes a single service, without leaving this screen. */
  onToggleService: (jobType: JobType, service: WidgetBootstrapResponse.ServiceItem) => void
  onRemoveService: (serviceId: string) => void
  showPrices: boolean
  total: number
  tyreLine: TyreLine | null
  /** Set when the vehicle's MOT is worth prompting about. */
  motPrompt: MotPrompt | null
  /** Reason-led suggestions, e.g. alignment after new tyres. */
  upsellPrompts: UpsellPrompt[]
}

function describeMotUrgency(daysRemaining: number): string {
  if (daysRemaining < 0) return 'Your MOT has expired'
  if (daysRemaining === 0) return 'Your MOT expires today'
  if (daysRemaining <= 31) return `Your MOT expires in ${daysRemaining} days`
  return 'Your MOT is due soon'
}

/**
 * The basket builder: what's in the booking, and what else could be.
 *
 * Everything happens here. Adding a job with more than one option expands it in
 * place rather than sending the customer back to the service screen — doing
 * that made them re-answer every other multi-option job they had already
 * chosen, tyres included, which is exactly the friction this step exists to
 * avoid.
 *
 * Sits after the job is settled but before the date is picked, the moment
 * customers are most willing to add work.
 */
export function ExtrasStep({
  available,
  basket,
  onToggleService,
  onRemoveService,
  showPrices,
  total,
  tyreLine,
  motPrompt,
  upsellPrompts,
}: ExtrasStepProps) {
  const [expandedKey, setExpandedKey] = useState<string | null>(null)

  const handleJobTap = (jobType: JobType) => {
    // One thing to book means there is nothing to choose: add it and be done.
    // Conditional services (an MOT retest) do not count as a choice.
    const main = primaryService(jobType)
    if (main) {
      onToggleService(jobType, main)
      return
    }
    setExpandedKey((current) => (current === jobType.key ? null : jobType.key))
  }

  const hasBooking = basket.length > 0 || tyreLine !== null

  return (
    <div className="ac-stack">
      {hasBooking ? (
        <section className="ac-stack ac-stack--tight" aria-label="Your booking so far">
          <h4 className="ac-section-label">Your booking</h4>
          <div className="ac-summary">
            {basket.map((item) => (
              <div key={item.serviceId} className="ac-summary__row">
                <span>{item.name}</span>
                <span className="ac-summary__value">
                  {showPrices && item.included ? <span>Included</span> : null}
                  {showPrices && !item.included && isUnconfirmedPrice(item.priceState) ? (
                    <span>{unconfirmedPriceLabel(item.priceState)}</span>
                  ) : showPrices && !item.included && item.price != null ? (
                    <span>{item.price > 0 ? formatMoney(item.price) : 'Free'}</span>
                  ) : null}
                  <button
                    type="button"
                    className="ac-summary__remove"
                    onClick={() => onRemoveService(item.serviceId)}
                    aria-label={`Remove ${item.name}`}
                  >
                    <TrashIcon size={16} />
                  </button>
                </span>
              </div>
            ))}

            {tyreLine ? (
              <div className="ac-summary__row">
                <span>{tyreLine.description}</span>
                {showPrices ? (
                  <span className="ac-summary__value">{formatMoney(tyreLine.total)}</span>
                ) : null}
              </div>
            ) : null}

            {showPrices ? (
              <div className="ac-summary__row ac-summary__row--total">
                <span>Total</span>
                <span>{total > 0 ? formatMoney(total) : 'Free'}</span>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      <div className="ac-stack ac-stack--tight">
        <p className="ac-eyebrow">Anything else?</p>
        <h3 className="ac-heading">While it&apos;s with us</h3>
        <p className="ac-prose">
          {hasBooking
            ? 'Adding work to the same visit saves you a second trip.'
            : 'Pick anything else you need.'}
        </p>
      </div>

      {motPrompt ? (
        <button
          type="button"
          className="ac-promo"
          onClick={() =>
            motPrompt.service
              ? onToggleService(motPrompt.jobType, motPrompt.service)
              : handleJobTap(motPrompt.jobType)
          }
          aria-label={`Add ${motPrompt.service?.name ?? motPrompt.jobType.label}, ${describeMotUrgency(motPrompt.daysRemaining)}`}
        >
          <span className="ac-promo__icon">
            <JobIcon name="mot" size={26} />
          </span>
          <span className="ac-promo__main">
            <span className="ac-promo__title">{describeMotUrgency(motPrompt.daysRemaining)}</span>
            <span className="ac-promo__detail">
              Expires {motPrompt.expiryLabel} · add it to this visit
            </span>
          </span>
          <span className="ac-promo__action">
            {showPrices && motPrompt.fromPrice != null
              ? formatMoney(motPrompt.fromPrice)
              : 'Add'}
          </span>
        </button>
      ) : null}

      {upsellPrompts.map((prompt) => (
        <button
          key={prompt.jobType.key}
          type="button"
          className="ac-promo ac-promo--quiet"
          onClick={() => handleJobTap(prompt.jobType)}
        >
          <span className="ac-promo__icon">
            <JobIcon name={prompt.icon} size={24} />
          </span>
          <span className="ac-promo__main">
            <span className="ac-promo__title">{prompt.title}</span>
            <span className="ac-promo__detail">{prompt.detail}</span>
          </span>
          <span className="ac-promo__action">
            {showPrices && prompt.jobType.fromPrice != null
              ? formatMoney(prompt.jobType.fromPrice)
              : showPrices && prompt.jobType.freeOption
                ? 'Free'
                : 'Add'}
          </span>
        </button>
      ))}

      {available.length > 0 ? (
        <ul className="ac-list">
          {available
            .filter((jobType) => !upsellPrompts.some((prompt) => prompt.jobType.key === jobType.key))
            .map((jobType) => {
            const expanded = expandedKey === jobType.key
            const multiple = jobType.bookable.length > 1
            const panelId = `ac-extra-${jobType.key.replace(/\s+/g, '-')}`

            return (
              <li key={jobType.key} className="ac-extra">
                <button
                  type="button"
                  className={`ac-extra__head${expanded ? ' ac-extra__head--open' : ''}`}
                  onClick={() => handleJobTap(jobType)}
                  aria-expanded={multiple ? expanded : undefined}
                  aria-controls={multiple ? panelId : undefined}
                >
                  <span className="ac-extra__icon" aria-hidden="true">
                    <JobIcon name={jobType.icon} size={22} />
                  </span>

                  <span className="ac-extra__main">
                    <span className="ac-option__name">{jobType.label}</span>
                    {multiple ? (
                      <span className="ac-option__desc">
                        {jobType.bookable.length} options to choose from
                      </span>
                    ) : jobType.bookable[0]?.description ? (
                      <span className="ac-option__desc">{jobType.bookable[0].description}</span>
                    ) : null}
                  </span>

                  <span className="ac-extra__meta">
                    {showPrices && jobType.fromPrice != null ? (
                      <span className="ac-option__price">{formatMoney(jobType.fromPrice)}</span>
                    ) : showPrices && jobType.freeOption ? (
                      <span className="ac-option__price">Free</span>
                    ) : null}
                    <span className={`ac-extra__chevron${expanded ? ' ac-extra__chevron--open' : ''}`} aria-hidden="true">
                      <ChevronRightIcon size={18} />
                    </span>
                  </span>
                </button>

                {multiple && expanded ? (
                  <ul className="ac-extra__options" id={panelId}>
                    {jobType.bookable.map((service) => {
                      const selected = basket.some((item) => item.serviceId === service.id)
                      const price = normalizePrice(service.price)
                      const duration = formatDuration(service.durationMinutes)

                      return (
                        <li key={service.id}>
                          <button
                            type="button"
                            className={`ac-option${selected ? ' ac-option--selected' : ''}`}
                            aria-pressed={selected}
                            onClick={() => onToggleService(jobType, service)}
                          >
                            <span className="ac-option__check" aria-hidden="true">
                              <CheckIcon size={15} />
                            </span>
                            <span className="ac-option__main">
                              <span className="ac-option__name">{service.name}</span>
                              {service.description ? (
                                <span className="ac-option__desc">{service.description}</span>
                              ) : null}
                            </span>
                            <span className="ac-option__meta">
                              {showPrices && price != null ? (
                                <span className="ac-option__price">
                                  {price > 0 ? formatMoney(price) : 'Free'}
                                </span>
                              ) : null}
                              {duration ? (
                                <span className="ac-option__duration">{duration}</span>
                              ) : null}
                            </span>
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                ) : null}
              </li>
            )
          })}
        </ul>
      ) : (
        <div className="ac-notice ac-notice--info">
          <span>Nothing else to add. You&apos;re all set.</span>
        </div>
      )}
    </div>
  )
}
